import { Component, effect, EventEmitter, Input, Output, Signal, signal, ViewChild } from '@angular/core';
import { GoogleMap, MapAdvancedMarker, MapCircle } from '@angular/google-maps';
import { faLocationCrosshairs } from '@fortawesome/pro-solid-svg-icons';
import { SharedModule } from '@app/shared/shared.module';
import { ButtonComponent } from '@app/shared/ui/button/button.component';

@Component({
  selector: 'c-google-map',
  imports: [GoogleMap, MapAdvancedMarker, MapCircle, SharedModule, ButtonComponent],
  templateUrl: './google-map.component.html',
  styleUrl: './google-map.component.scss',
})
export class GoogleMapComponent {
  @Input() center: Signal<google.maps.LatLngLiteral>;
  @Input() circleOptions: google.maps.CircleOptions = {
    fillColor: '#2346DD',
    fillOpacity: 0.1,
    strokeColor: '#223BB1',
    strokeWeight: 2,
    clickable: false,
  };
  @Input() mapOptions: google.maps.MapOptions = {
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false
  };
  @Input() mapRadius: Signal<number> = signal(0);
  @Input() markerOptions: google.maps.marker.AdvancedMarkerElementOptions = {
    gmpDraggable: false,
    gmpClickable: false,
  };
  @Input() zoom: Signal<number> = signal(15);
  @Input() showCurrentLocationButton = true;
  @Input() currentLocationActive = false;

  @Output() mapReady = new EventEmitter<void>();
  @Output() currentLocationRequested = new EventEmitter<void>();

  @ViewChild(GoogleMap) googleMap: GoogleMap;
  @ViewChild(MapCircle) mapCircle: MapCircle;

  readonly currentLocationIcon = faLocationCrosshairs;
  protected readonly mapLoaded = signal(false);

  onMapReady(): void {
    this.mapLoaded.set(true);
    this.mapReady.emit();
  }

  constructor() {
    effect(() => {
      this.center(); 
      this.mapRadius();

      if (this.mapRadius() !== 0) {
        const bounds = this.boundsForCircle(this.center(), this.mapRadius());
        if (!bounds) return;

        this.googleMap?.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 } as any);
      }
    });
  }

  /**
   * Calculates the geographic bounds (bounding box) for a circle on the map.
   * This is useful for fitting the map view to show the entire circle.
   * 
   * @param c - The center point of the circle (latitude and longitude)
   * @param radiusMeters - The radius of the circle in meters
   * @returns A LatLngBounds object representing the bounding box that contains the circle
   */
  private boundsForCircle(c: google.maps.LatLngLiteral, radiusMeters: number): google.maps.LatLngBounds {
    // Earth's radius in meters
    const R = 6378137;
    
    // Angular distance in radians (arc length / radius)
    const d = radiusMeters / R;

    // Convert center coordinates from degrees to radians
    const latRad = (c.lat * Math.PI) / 180;
    const lngRad = (c.lng * Math.PI) / 180;

    // Calculate the minimum and maximum latitudes by adding/subtracting the angular distance
    const minLat = latRad - d;
    const maxLat = latRad + d;

    // Calculate the change in longitude, accounting for latitude (circles are smaller near the poles)
    const deltaLng = Math.asin(Math.sin(d) / Math.cos(latRad));
    const minLng = lngRad - deltaLng;
    const maxLng = lngRad + deltaLng;

    // Create the southwest and northeast corners of the bounding box (convert back to degrees)
    const sw = new google.maps.LatLng((minLat * 180) / Math.PI, (minLng * 180) / Math.PI);
    const ne = new google.maps.LatLng((maxLat * 180) / Math.PI, (maxLng * 180) / Math.PI);
    
    // Return the bounds created from the two corner points
    return new google.maps.LatLngBounds(sw, ne);
  }
}
